import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Boxes, Truck, Ship, Map, Layers, BarChart3 } from "lucide-react";
import { Eyebrow, SectionHeading, FadeUp } from "../components/site/Primitives";
import SceneImage from "../components/site/SceneImage";

const FEATURES = [
  { icon: Boxes, title: "Bulk ordering", body: "Pallet-level and container-level wholesale across our core SKU range." },
  { icon: Truck, title: "Monthly recurring supply", body: "Standing orders that flex with your menu and seasonal demand." },
  { icon: Ship, title: "Container-load imports", body: "Direct sourcing from vetted bagasse, kraft and birchwood manufacturers." },
  { icon: BarChart3, title: "Stock planning", body: "Rolling forecasts and reorder triggers so you never run out at peak service." },
  { icon: Map, title: "Ghana & UK market focus", body: "Local stockholding in Accra and London. Pricing optimised for both markets." },
  { icon: Layers, title: "Bespoke solutions", body: "Custom sleeve sizing, sticker formats and palletisation built around your operation." },
];

export default function WholesalePage() {
  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-24 lg:pb-32 kraft-texture overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
          <SceneImage
            scene="wholesale-warehouse"
            eager
            className="h-full w-full object-cover"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09] via-[#0A0A09]/85 to-transparent" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>Supplier / Wholesale</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              A recurring supply partner for{" "}
              <span className="font-serif-display italic font-medium text-[#E85D2F]">
                serious
              </span>{" "}
              food operations.
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#F7F5F0]/75 leading-relaxed font-light">
              We work with restaurant groups, hotel chains, supermarkets and
              caterers on monthly volumes — not one-off pallet drops.
              Container-load economics, local stock, predictable pricing.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-24 lg:py-32 bg-[#121211] border-y border-[#F7F5F0]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading
            eyebrow="What you get"
            title="Wholesale, structured for growth."
          />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full rounded-3xl border border-[#E85D2F]/15 bg-[#0A0A09] p-7 hover:border-[#E85D2F]/40 hover:-translate-y-1 transition-all duration-500">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1A1A18] text-[#E85D2F]">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-grotesk text-xl font-bold text-[#F7F5F0] tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#F7F5F0]/65 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Stats / Markets */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Eyebrow>Markets</Eyebrow>
            <h2 className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
              Accra to London. One supply chain.
            </h2>
            <p className="mt-6 text-[#F7F5F0]/70 leading-relaxed font-light max-w-xl">
              Our dual-market focus means you get sourcing strength from Ghana
              combined with European-grade logistics, payment and quality
              assurance from our UK operation.
            </p>
          </div>
          <FadeUp className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "Container shipments / yr", v: "Multi-FCL" },
                { k: "Active SKUs", v: "6 + bespoke" },
                { k: "Markets served", v: "GH · UK" },
                { k: "Lead time, standard SKU", v: "2–4 wks" },
              ].map((s) => (
                <div
                  key={s.k}
                  className="rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-7"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold">
                    {s.k}
                  </p>
                  <p className="mt-4 font-grotesk text-3xl font-black text-[#F7F5F0] tracking-tighter">
                    {s.v}
                  </p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="rounded-[2rem] bg-[#1A1A18] p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <h3 className="font-grotesk text-3xl md:text-5xl font-black tracking-[-0.03em] leading-[0.95] text-[#F7F5F0]">
                Request{" "}
                <span className="font-serif-display italic font-medium text-[#E85D2F]">
                  wholesale pricing
                </span>{" "}
                today.
              </h3>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact?subject=wholesale"
                data-testid="wholesale-cta-bottom"
                className="inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-medium hover:bg-[#F47A4A] transition-colors"
              >
                Get a wholesale quote
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
